import { useRef, useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowsLeftRightIcon } from '@phosphor-icons/react/dist/csr/ArrowsLeftRight';

import { Button } from '../../design-system/primitives/Button/Button';
import { Input } from '../../design-system/primitives/Input/Input';
import {
  ApiError,
  NETWORK_ERROR_CODE,
  PAGE_EXPIRED_CODE,
  TOO_MANY_REQUESTS_CODE,
  VALIDATION_FAILED_CODE,
} from '../../lib/api/client';
import styles from './ChallengeForm.module.css';

/**
 * The shape both halves of the second step share.
 *
 * `MfaChallengeScreen` and `RecoveryCodeScreen` differ in exactly three things:
 * the field, the copy, and which API call they make. Everything else — the
 * empty-from-first-frame live regions, the single undifferentiated failure, the
 * support reference, the in-flight guard, the `preventDefault` that runs before
 * anything that can throw — is the same, and it is the part that is easy to get
 * subtly wrong twice.
 *
 * ## What the backend already decided, and this form must not undo
 *
 * The API answers ONE `422`, with one message, for an unknown token, a wrong
 * code, an expired challenge, a challenge that has burnt its five attempts, a
 * challenge issued for enrolment, and an account disabled since the password
 * step. The OpenAPI document says it in capitals: *"Do not build a client that
 * tries to tell them apart; it cannot, deliberately."* So there is one failure
 * string here and no branch that could grow into a second.
 *
 * `error.message` from the envelope is never rendered. It is a fixed English
 * string per code that the specification marks "not localised"; painting it
 * puts English in front of an Arabic agent. This branches on `error.code` and
 * supplies translated copy, exactly as `LoginPage` does.
 *
 * ## Why the in-flight guard is load-bearing
 *
 * A wrong code spends one of the challenge's five attempts AND one of the
 * account's login attempts. A double-submitted click therefore costs two of
 * five, and a user who double-clicks twice has burnt the challenge. The guard
 * is not a nicety about spinners.
 */

export interface ChallengeFormProps {
  title: string;
  intro: string;
  /** Field label. Also what the tests find the input by. */
  label: string;
  /** Text under the field, standing. */
  hint?: string;
  /** `numeric` pins `dir="ltr"` and tabular figures; see Input. */
  numeric?: boolean;
  autoComplete: string;
  maxLength?: number;
  /**
   * Strips what the field must not carry, on every keystroke.
   *
   * Applied on CHANGE and not on blur, which is the opposite of the design
   * system's validate-on-blur rule — and deliberately so. This is not
   * validation telling the agent they are wrong; it is the field accepting the
   * thing authenticators actually display ("123 456") without turning a correct
   * paste into a spent attempt.
   */
  sanitise?: (raw: string) => string;
  /** Whether the current value may be submitted at all. */
  canSubmit: (value: string) => boolean;
  submitLabel: string;
  /** The submit button's leading 16px glyph (every button carries one). */
  submitIcon: ReactNode;
  /** The failure copy for this form. One string, never field-scoped. */
  failureMessage: string;
  onSubmit: (value: string) => Promise<unknown>;
  /** Called once the submission resolves. The parent owns what happens next. */
  onSuccess?: () => void;
  /** The other route out of this screen. */
  alternate: { label: string; onSelect: () => void };
  /** Rendered between the intro and the field. Used by the enrolment screen. */
  children?: ReactNode;
}

export function ChallengeForm({
  title,
  intro,
  label,
  hint,
  numeric = false,
  autoComplete,
  maxLength,
  sanitise,
  canSubmit,
  submitLabel,
  submitIcon,
  failureMessage,
  onSubmit,
  onSuccess,
  alternate,
  children,
}: ChallengeFormProps) {
  const { t } = useTranslation();

  const [value, setValue] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const [reference, setReference] = useState<string | null>(null);
  const [progress, setProgress] = useState<string | null>(null);

  /**
   * Read inside the submit handler, where a stale render's closure would let a
   * second attempt through. `submitting` alone is not enough: two clicks inside
   * one React batch both see `false`.
   */
  const inFlight = useRef(false);

  function describe(error: ApiError): string {
    switch (error.code) {
      case VALIDATION_FAILED_CODE:
        return failureMessage;
      case TOO_MANY_REQUESTS_CODE:
        return t('auth.throttled');
      case PAGE_EXPIRED_CODE:
        return t('auth.sessionExpired');
      case NETWORK_ERROR_CODE:
        return t('auth.unreachable');
      default:
        return t('auth.unexpected');
    }
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    // FIRST statement. Everything below can throw, and a default-action submit
    // that got through would navigate — putting the code, and on the recovery
    // screen a single-use credential, into the address bar and the referrer.
    event.preventDefault();

    if (inFlight.current || !canSubmit(value)) return;

    inFlight.current = true;
    setSubmitting(true);
    setFailure(null);
    setReference(null);
    setProgress(t('auth.mfa.checking'));

    try {
      await onSubmit(value);
      setValue('');
      setProgress(t('auth.signedIn'));
      onSuccess?.();
    } catch (error) {
      setProgress(null);

      if (!(error instanceof ApiError)) {
        // A fault in our own code, not a refusal from the API. The agent must
        // be told the form did nothing, and the fault must NOT be disguised as
        // a credential problem — that would blame the user for our bug and hide
        // it from every error reporter.
        setFailure(t('auth.unexpected'));
        throw error;
      }

      setFailure(describe(error));
      setReference(error.requestId ?? null);
    } finally {
      inFlight.current = false;
      setSubmitting(false);
    }
  }

  return (
    <main className={styles['page']} data-testid="challengePage">
      <form
        className={styles['card']}
        // If anything above `preventDefault` ever throws, the browser performs
        // the default submit — and a form with no `method` defaults to GET,
        // which would serialise the credential into the URL.
        method="post"
        noValidate
        onSubmit={(event) => void handleSubmit(event)}
      >
        <h1 className={styles['title']}>{title}</h1>
        <p className={styles['intro']}>{intro}</p>

        {children}

        {/*
          Both live regions are mounted from the first frame, EMPTY. Assistive
          technology announces changes inside a region it is already watching; a
          region inserted with its message already in it gives the platform
          nothing to diff and may be announced late or not at all.
        */}
        <div
          className={styles['alert']}
          role="alert"
          aria-atomic="true"
          data-testid="challengeAlert"
        >
          {failure === null ? null : <span className={styles['alertText']}>{failure}</span>}
          {reference === null ? null : (
            <span className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              {/* A request id is a code identifier: `<bdi dir="ltr">` isolates
                  its bidi run so the Arabic sentence around it still reads
                  right-to-left. */}
              <bdi dir="ltr" data-testid="challengeRequestId">
                {reference}
              </bdi>
            </span>
          )}
        </div>

        <div
          className={styles['progress']}
          role="status"
          aria-atomic="true"
          data-testid="challengeStatus"
        >
          {progress}
        </div>

        <Input
          label={label}
          hint={hint}
          width="fill"
          numeric={numeric}
          autoComplete={autoComplete}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          maxLength={maxLength}
          required
          value={value}
          onChange={(event) => {
            setValue(sanitise ? sanitise(event.target.value) : event.target.value);
          }}
        />

        <Button
          type="submit"
          loading={submitting}
          disabled={!canSubmit(value)}
          className={styles['submit']}
          icon={submitIcon}
        >
          {submitLabel}
        </Button>

        <Button
          type="button"
          variant="ghost"
          className={styles['alternate']}
          icon={<ArrowsLeftRightIcon size={16} />}
          onClick={alternate.onSelect}
        >
          {alternate.label}
        </Button>
      </form>
    </main>
  );
}
